import React from 'react';
import FormField from '../shared/FormField';
import SelectField from '../shared/SelectField';
import CheckboxField from '../shared/CheckboxField';

const ReporterInformation = ({ 
  formData, 
  updateFormData, 
  departments, 
  errors = {},
  loading = false 
}) => {
  const handleFieldChange = (updates) => {
    updateFormData(updates);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Reporter Information</h2>
            <p className="text-sm text-gray-600 mt-1">
              Please provide your contact information. This information helps us follow up on the report.
            </p>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Your Name"
          name="reporter_name"
          type="text"
          required
          value={formData.reporter_name}
          onChange={handleFieldChange}
          error={errors.reporter_name}
          placeholder="Enter your full name"
          maxLength={100}
          disabled={loading}
        />

        <FormField
          label="Position/Role"
          name="reporter_position"
          type="text"
          value={formData.reporter_position}
          onChange={handleFieldChange}
          error={errors.reporter_position}
          placeholder="e.g., Aircraft Mechanic, Line Technician"
          maxLength={100}
          disabled={loading}
        />

        <FormField
          label="Employee ID"
          name="reporter_employee_id"
          type="text"
          value={formData.reporter_employee_id}
          onChange={handleFieldChange}
          error={errors.reporter_employee_id}
          placeholder="Your employee identification number"
          maxLength={50}
          disabled={loading}
        />

        <SelectField
          label="Department"
          name="reporter_department_id"
          required
          value={formData.reporter_department_id}
          onChange={handleFieldChange}
          options={departments}
          optionValue="id"
          optionLabel="dept_name"
          error={errors.reporter_department_id}
          placeholder="Select your department"
          loading={loading}
          disabled={loading}
        />
      </div>

      {/* Contact Information */}
      <div className="border-t border-gray-100 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Contact Email"
            name="reporter_email"
            type="email"
            value={formData.reporter_email}
            onChange={handleFieldChange}
            error={errors.reporter_email}
            placeholder="your.email@company.com"
            maxLength={255}
            helpText="Used for follow-up communication about this report"
            disabled={loading}
          />

          <FormField
            label="Contact Phone"
            name="reporter_phone"
            type="tel"
            value={formData.reporter_phone}
            onChange={handleFieldChange}
            error={errors.reporter_phone}
            placeholder="+66 XX XXX XXXX"
            maxLength={50}
            helpText="Include country/area code"
            disabled={loading}
          />
        </div>
      </div>

      {/* Confidentiality Options */}
      <div className="border-t border-gray-100 pt-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <svg className="flex-shrink-0 w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-900">Report Confidentiality</h4>
              <p className="text-sm text-blue-700 mt-1 mb-4">
                Choose how your identity should be handled in this report.
              </p>

              <div className="space-y-3">
                <CheckboxField
                  name="is_confidential"
                  label="This is a confidential report"
                  checked={formData.is_confidential}
                  onChange={handleFieldChange}
                  description="Your identity will be protected. Only safety management will know who submitted this report."
                  disabled={loading}
                />

                {formData.is_confidential && (
                  <div className="ml-8 p-3 bg-amber-50 border border-amber-200 rounded">
                    <div className="flex items-start space-x-2">
                      <svg className="flex-shrink-0 w-4 h-4 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <h5 className="text-xs font-medium text-amber-800">Confidential Report Process</h5>
                        <ul className="text-xs text-amber-700 mt-1 space-y-1">
                          <li>• Your identity will be known only to safety management</li>
                          <li>• A redacted copy will be created for investigation/workflow</li>
                          <li>• You will receive a confidential reference number for follow-up</li>
                          <li>• All access to your identity is logged and audited</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Department Information Display */}
      {formData.reporter_department_id && departments.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Department Information</h4>
          {(() => {
            const selectedDept = departments.find(d => d.id === formData.reporter_department_id);
            if (!selectedDept) return null;
            
            return (
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Department:</strong> {selectedDept.dept_name}</p>
                <p><strong>Code:</strong> {selectedDept.dept_code}</p>
                {selectedDept.dept_description && (
                  <p><strong>Description:</strong> {selectedDept.dept_description}</p>
                )}
                {selectedDept.is_safety_critical && (
                  <div className="flex items-center space-x-1 text-amber-600">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>Safety Critical Department</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Escalation timeline: {selectedDept.escalation_hours || 24} hours
                </p>
              </div>
            );
          })()}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-sm text-gray-600">Loading department information...</span>
        </div>
      )}
    </div>
  );
};

export default ReporterInformation;
